import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock } from '@fortawesome/free-solid-svg-icons';

/**
 * The Expense modal's per-row Revert.
 *
 * The SERVER decides whether a row may be reverted: /reports/stockout-details
 * puts `revert: { allowed, code, reason }` on every row (a confirmed closing
 * count, the 30-day window, a count in progress, a frozen banquet). This button
 * only renders that verdict — a locked row shows the stock-in lock's muted
 * style with the reason as its tooltip — and POST /wastage/:id/revert re-checks
 * everything, so a stale verdict can never let a revert through.
 *
 * A row from an older payload with no verdict stays revertable: the endpoint
 * still refuses what it must.
 *
 * The row itself goes to onRevert as the third argument, so the confirm dialog
 * can name a banquet wastage row's event (banquetEventId / functionName /
 * eventDate) and the amount its gross profit gets back.
 */
const WastageRevertButton = ({ row, reverting = false, onRevert }) => {
  if (!row || !row.stock_out_id) return null;

  const locked = row.revert && row.revert.allowed === false;
  if (locked) {
    const reason = row.revert.reason || 'This wastage can no longer be reverted.';
    return (
      <button
        type="button"
        disabled
        aria-label={`Revert locked: ${reason}`}
        title={reason}
        onClick={(e) => e.stopPropagation()}
        style={{
          background: '#f3f4f6',
          color: '#9ca3af',
          border: '1px solid #e5e7eb',
          borderRadius: '6px',
          padding: '3px 8px',
          fontSize: '11px',
          fontWeight: 500,
          cursor: 'not-allowed',
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          opacity: 0.8,
          marginTop: '4px',
        }}
      >
        <FontAwesomeIcon icon={faLock} />
        Revert
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        if (onRevert) onRevert(row.stock_out_id, row.itemName, row);
      }}
      disabled={reverting}
      style={{
        background: reverting ? '#d1d5db' : '#fee2e2',
        color: reverting ? '#6b7280' : '#dc2626',
        border: '1px solid #fecaca',
        borderRadius: '6px',
        padding: '3px 8px',
        fontSize: '11px',
        fontWeight: 500,
        cursor: reverting ? 'not-allowed' : 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '3px',
        transition: 'all 0.2s',
        marginTop: '4px',
      }}
      onMouseEnter={(e) => {
        if (!reverting) {
          e.currentTarget.style.background = '#fecaca';
          e.currentTarget.style.borderColor = '#fca5a5';
        }
      }}
      onMouseLeave={(e) => {
        if (!reverting) {
          e.currentTarget.style.background = '#fee2e2';
          e.currentTarget.style.borderColor = '#fecaca';
        }
      }}
      title="Revert this wastage record and restore inventory"
    >
      {reverting ? 'Reverting...' : 'Revert'}
    </button>
  );
};

export default WastageRevertButton;
